
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  schema?: object;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  schema,
}: SEOHeadProps) {
  const location = useLocation();
  // Ensure siteUrl is always a string; fallback to a safe default if env var is missing.
  const siteUrl = (import.meta.env?.VITE_SITE_URL as string) ?? 'https://example.com';

  useEffect(() => {
    // ---- Title -------------------------------------------------------------
    if (title) {
      document.title = title;
    }

    // ---- Meta description --------------------------------------------------
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);
    }

    // ---- Meta keywords -----------------------------------------------------
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }

    // ---- Canonical URL ------------------------------------------------------
    // Fixed template literal (removed escaped backticks)
    const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonicalUrl;

    // ---- Last‑modified -------------------------------------------------------
    const today = new Date().toISOString().split('T')[0];
    let metaLastModified = document.querySelector('meta[name="last-modified"]');
    if (!metaLastModified) {
      metaLastModified = document.createElement('meta');
      metaLastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(metaLastModified);
    }
    metaLastModified.setAttribute('content', today);

    // ---- Open Graph & Twitter tags ------------------------------------------
    if (title) {
      // og:title
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', title);

      // twitter:title
      let twitterTitle = document.querySelector('meta[property="twitter:title"]');
      if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.setAttribute('property', 'twitter:title');
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.setAttribute('content', title);
    }

    if (description) {
      // og:description
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', description);

      // twitter:description
      let twitterDescription = document.querySelector('meta[property="twitter:description"]');
      if (!twitterDescription) {
        twitterDescription = document.createElement('meta');
        twitterDescription.setAttribute('property', 'twitter:description');
        document.head.appendChild(twitterDescription);
      }
      twitterDescription.setAttribute('content', description);
    }

    // og:url
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', canonicalUrl);

    // twitter:url
    let twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (!twitterUrl) {
      twitterUrl = document.createElement('meta');
      twitterUrl.setAttribute('property', 'twitter:url');
      document.head.appendChild(twitterUrl);
    }
    twitterUrl.setAttribute('content', canonicalUrl);

    // ---- Schema.org JSON‑LD -------------------------------------------------
    if (schema) {
      let scriptTag = document.querySelector(
        'script[type="application/ld+json"]#page-schema'
      );
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.setAttribute('id', 'page-schema');
        document.head.appendChild(scriptTag);
      }
      try {
        scriptTag.textContent = JSON.stringify(schema);
      } catch (e) {
        // Fail‑safe: if schema cannot be stringified, remove the tag to avoid invalid JSON‑LD.
        console.error('Failed to stringify schema for SEOHead:', e);
        scriptTag.remove();
      }
    }

    // Cleanup on unmount
    return () => {
      const scriptTag = document.querySelector(
        'script[type="application/ld+json"]#page-schema'
      );
      if (scriptTag) {
        scriptTag.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    description,
    keywords,
    canonical,
    schema,
    location.pathname,
    siteUrl,
  ]);

  // This component only manipulates the document head; it renders nothing.
  return null;
}
